import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Business, Groups, Hub, Person, Share } from '@mui/icons-material';
import companyAdminService from '../../services/companyAdminService';
import partnerAdminService from '../../services/partnerAdminService';
import resourceSharingService from '../../services/resourceSharingService';
import { usePermissions } from '../../context/PermissionsContext';
import { PERMISSIONS, PARTNER_PERMISSIONS } from '../../constants/permissions';
import { SHARING_COPY } from '../../constants/partnerCopy';
import { SHARED_KB_COPY } from '../../constants/sharedKnowledgeCopy';
import { describeApiError, mapApiError } from '../../utils/apiError';
import { principalLabel } from './sharingLabels';
import styles from './ResourceSharing.module.scss';

const resourceLabels = {
  agent: 'agent',
  folder_dim1: 'organizational knowledge folder',
  folder_dim3: 'personal knowledge folder',
  composio_app: 'integration',
  custom_tool: 'custom tool',
};

const getId = (item) => item?.id ?? item?.user_id ?? item?.role_id;
const userName = (user) =>
  user?.name ||
  [user?.first_name, user?.last_name].filter(Boolean).join(' ') ||
  user?.email ||
  `User ${getId(user)}`;

// The share schema declares ids as int — coerce and never rely on Pydantic
// accepting numeric strings. Returns null when there is no usable id.
const toNumericId = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

// Principal id of a parent share row (GET /shares/resource/{type}/{id}).
// ShareResponse has no `principal_id` field — it uses typed columns
// (principal_role_id / principal_user_id / principal_company_id /
// principal_partner_id), with principal_display.id as the uniform copy.
const sharePrincipalId = (share) => {
  if (!share) return null;
  const typedField = {
    role: 'principal_role_id',
    user: 'principal_user_id',
    company: 'principal_company_id',
    partner: 'principal_partner_id',
  }[share.principal_type];
  return toNumericId((typedField ? share[typedField] : null) ?? share.principal_display?.id);
};

// Which targets must carry a principal_id. Inside one company, "company" means
// "the owner's company" and needs none; across a partner's companies a company
// target names one specific company. "partner" never needs one.
const needsPrincipalId = (principalType, scope) =>
  principalType === 'role' || principalType === 'user' || (principalType === 'company' && scope === 'partner');

/**
 * Single source of truth for POST /shares target objects:
 *   company (within)   → { principal_type: 'company' }          (no id)
 *   company (partner)  → { principal_type: 'company', principal_id: int }
 *   partner            → { principal_type: 'partner' }          (no id)
 *   role / user        → { principal_type, principal_id: int }  (id REQUIRED)
 * Returns null instead of emitting a target that needs an id but has none —
 * the backend 400s on those.
 */
const buildTarget = (principalType, id, scope = 'company') => {
  if (principalType === 'partner') return { principal_type: 'partner' };
  if (!needsPrincipalId(principalType, scope)) return { principal_type: principalType };
  const principalId = toNumericId(id);
  if (principalId === null) return null;
  return { principal_type: principalType, principal_id: principalId };
};

const audienceIcon = (type) =>
  type === 'partner' ? Hub : type === 'company' ? Business : type === 'role' ? Groups : Person;

const AudienceOption = ({ audience, checked, onChange }) => {
  const Icon = audienceIcon(audience.type);
  return (
    <FormControlLabel
      className={styles.audienceOption}
      control={<Checkbox checked={checked} onChange={onChange} size="small" />}
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Icon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 650 }}>{audience.name}</Typography>
            <Typography variant="caption" color="text.secondary">{audience.subtitle}</Typography>
          </Box>
        </Box>
      }
    />
  );
};

// §6.5 error table (partner mode) + the in-company refusal a client resource gets.
const shareErrors = (partnerName) => [
  { status: 403, match: /Missing permission: partner_sharing/i, message: "You don't have permission to share across companies." },
  { status: 403, match: /own partner's companies/i, message: "Can only share resources of your own partner's companies" },
  { status: 403, match: /within your own company/i, message: 'Can only share resources within your own company' },
  { status: 400, match: /does not belong to a partner/i, message: "This resource belongs to a company that isn't under a partner." },
  {
    status: 400,
    match: /(Company|User) \S+ does not belong to the partner/i,
    message: `One of the selected companies or people isn't part of ${partnerName} any more.`,
  },
];

/**
 * Create OR edit a share. Pass `initialShare` (a parent share row from
 * GET /shares/resource/{type}/{id}) to open in edit mode: everything is prefilled
 * from the share, the audience is locked (it cannot be patched — changing it means
 * revoke + share again), and saving goes through the normal POST /shares/, which
 * upserts server-side.
 *
 * Two scopes (Partner tier guide §6):
 *   - 'company' (within the owner's company) — unchanged; only possible when the
 *     sharer is in the owner's company.
 *   - 'partner' (across the partner's companies) — for holders of
 *     partner_sharing: targets are "all companies", specific companies (incl. the
 *     partner's own staff) or specific people; no roles; private memory only;
 *     Composio defaults to each person's own account; custom tools are opt-in.
 * `resource.ownerCompanyId` / `ownerCompanyName` say whose resource this is
 * (omit for the sharer's own company); `defaultScope` picks the opening mode.
 */
const ResourceShareDialog = ({ open, resource, initialShare = null, defaultScope = null, onClose, onSuccess }) => {
  const { has, hasPartner, ready: permissionsReady, company, partner, isPartnerStaff } = usePermissions();
  const isEditing = Boolean(initialShare);
  const isAgent = resource?.type === 'agent';
  const partnerName = partner?.name || 'your partner';
  const homeCompanyId = partner?.home_company_id ?? null;

  // Whose resource is this, and which scopes can this sharer use?
  const ownerCompanyId = resource?.ownerCompanyId ?? null;
  const canWithin =
    ownerCompanyId == null || (company?.id != null && Number(ownerCompanyId) === Number(company.id));
  const canAcross = permissionsReady && hasPartner(PARTNER_PERMISSIONS.SHARING);
  const editScope = isEditing ? (initialShare.partner_id != null ? 'partner' : 'company') : null;
  const initialScope =
    editScope ||
    ((defaultScope === 'partner' || !canWithin) && canAcross ? 'partner' : 'company');
  const withinName = isPartnerStaff ? partnerName : company?.name || 'your company';
  const showModeSwitch = !isEditing && canAcross && canWithin;

  // Edit mode never opens the audience pickers, so the directory calls (which
  // 403 without the matching admin key) are skipped entirely.
  const canLoadUsers = !isEditing && permissionsReady && canWithin && has(PERMISSIONS.USER_MANAGEMENT);
  const canLoadRoles = !isEditing && permissionsReady && canWithin && has(PERMISSIONS.ROLE_MANAGEMENT);
  const canLoadPartnerCompanies = !isEditing && canAcross && hasPartner(PARTNER_PERMISSIONS.COMPANIES);
  const canLoadPartnerUsers = !isEditing && canAcross && hasPartner(PARTNER_PERMISSIONS.USERS);

  // Editing is only possible when the row exposes the principal's id — without
  // it a re-share would 400 on the backend.
  const editPrincipalMissing =
    isEditing &&
    needsPrincipalId(initialShare.principal_type, editScope) &&
    sharePrincipalId(initialShare) === null;

  const [scope, setScope] = useState(initialScope);
  const isPartnerMode = scope === 'partner';
  const [step, setStep] = useState(0);
  const [companyAudiences, setCompanyAudiences] = useState([]);
  const [partnerAudiences, setPartnerAudiences] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [permission, setPermission] = useState('read');
  const [memoryMode, setMemoryMode] = useState('per_user');
  const [authMode, setAuthMode] = useState('with_auth');
  const [attachments, setAttachments] = useState(null);
  const [childSettings, setChildSettings] = useState({});
  const [includeTools, setIncludeTools] = useState({}); // partner mode: custom tools are opt-in
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [companyDirWarnings, setCompanyDirWarnings] = useState([]);
  const [partnerDirWarnings, setPartnerDirWarnings] = useState([]);
  const [pendingWarnings, setPendingWarnings] = useState(null);
  const [doneResult, setDoneResult] = useState(null); // partner mode: 201 carrying warnings[]
  const [reloadNonce, setReloadNonce] = useState(0);

  const audiences = isPartnerMode ? partnerAudiences : companyAudiences;
  const directoryWarnings = isPartnerMode ? partnerDirWarnings : companyDirWarnings;
  // An agent share does not carry the owner's Customer Data (D3) folders: each
  // recipient chooses their own. The exception is a shared-memory share, where
  // every recipient builds one common history and so keeps running on the
  // owner's folders. Partner shares are always per-user.
  const sharesOwnerDim3 = !isPartnerMode && memoryMode === 'shared';

  useEffect(() => {
    if (!open || !resource?.id) return undefined;
    let cancelled = false;
    const composioDefault = initialScope === 'partner' ? 'without_auth' : 'with_auth';
    setScope(initialScope);
    setStep(0);
    setSelected(new Set());
    setPermission(initialShare?.permission || 'read');
    setMemoryMode(initialScope === 'partner' ? 'per_user' : initialShare?.options?.memory_mode || 'per_user');
    setAuthMode(initialShare?.options?.auth_mode || composioDefault);
    setAttachments(null);
    setChildSettings({});
    setIncludeTools({});
    setSearch('');
    setError('');
    setCompanyDirWarnings([]);
    setPartnerDirWarnings([]);
    setPendingWarnings(null);
    setDoneResult(null);
    setLoading(true);

    (async () => {
      const [usersResult, rolesResult, pCompaniesResult, pUsersResult, attachmentResult] = await Promise.allSettled([
        canLoadUsers
          ? companyAdminService.listUsers({ limit: 200, offset: 0 })
          : Promise.reject(new Error('Company user directory permission is not available.')),
        canLoadRoles
          ? companyAdminService.listRoles({ limit: 200, offset: 0 })
          : Promise.reject(new Error('Company role directory permission is not available.')),
        canLoadPartnerCompanies
          ? partnerAdminService.listCompanies({ limit: 200 })
          : Promise.reject(new Error('Partner company directory permission is not available.')),
        canLoadPartnerUsers
          ? partnerAdminService.listUsers({ limit: 200 })
          : Promise.reject(new Error('Partner user directory permission is not available.')),
        isAgent ? resourceSharingService.getAgentAttachments(resource.id) : Promise.resolve(null),
      ]);
      if (cancelled) return;

      if (isEditing) {
        // Audience is fixed while editing — no pickers, no directory warnings.
        setCompanyAudiences([]);
        setPartnerAudiences([]);
      } else {
        if (canWithin) {
          const usersData = usersResult.status === 'fulfilled' ? usersResult.value : {};
          const rolesData = rolesResult.status === 'fulfilled' ? rolesResult.value : {};
          const users = Array.isArray(usersData?.users) ? usersData.users : Array.isArray(usersData) ? usersData : [];
          const roles = Array.isArray(rolesData?.roles) ? rolesData.roles : Array.isArray(rolesData) ? rolesData : [];
          const unavailable = [];
          if (usersResult.status === 'rejected') unavailable.push('company users');
          if (rolesResult.status === 'rejected') unavailable.push('company roles');
          setCompanyDirWarnings(unavailable);
          setCompanyAudiences([
            { key: 'company', type: 'company', name: 'Everyone in the company', subtitle: 'Company' },
            ...roles.map((role) => ({
              key: `role:${getId(role)}`,
              type: 'role',
              id: getId(role),
              name: role.name || `Role ${getId(role)}`,
              subtitle: 'Role',
            })),
            ...users.map((user) => ({
              key: `user:${getId(user)}`,
              type: 'user',
              id: getId(user),
              name: userName(user),
              subtitle: user.email || 'User',
            })),
          ]);
        }
        if (canAcross) {
          const pCompaniesData = pCompaniesResult.status === 'fulfilled' ? pCompaniesResult.value : {};
          const pUsersData = pUsersResult.status === 'fulfilled' ? pUsersResult.value : {};
          const companies = Array.isArray(pCompaniesData?.companies) ? pCompaniesData.companies : Array.isArray(pCompaniesData) ? pCompaniesData : [];
          const people = Array.isArray(pUsersData?.users) ? pUsersData.users : Array.isArray(pUsersData) ? pUsersData : [];
          const unavailable = [];
          if (pCompaniesResult.status === 'rejected') unavailable.push("the partner's companies");
          if (pUsersResult.status === 'rejected') unavailable.push("the partner's people");
          setPartnerDirWarnings(unavailable);
          const companyName = (id) =>
            homeCompanyId != null && Number(id) === Number(homeCompanyId)
              ? `${partnerName} · direct users`
              : companies.find((c) => Number(c.id) === Number(id))?.name;
          setPartnerAudiences([
            { key: 'partner', type: 'partner', name: SHARING_COPY.targetAll(partnerName), subtitle: SHARING_COPY.targetAllHelp(partnerName) },
            ...(homeCompanyId != null
              ? [{ key: `company:${homeCompanyId}`, type: 'company', id: homeCompanyId, name: `${partnerName} · direct users`, subtitle: 'Company' }]
              : []),
            ...companies.map((c) => ({ key: `company:${c.id}`, type: 'company', id: c.id, name: c.name, subtitle: 'Company' })),
            ...people.map((user) => ({
              key: `user:${getId(user)}`,
              type: 'user',
              id: getId(user),
              name: userName(user),
              subtitle: [user.email, companyName(user.company_id)].filter(Boolean).join(' · ') || 'Person',
            })),
          ]);
        }
      }

      if (attachmentResult.status === 'fulfilled' && attachmentResult.value) {
        const data = attachmentResult.value;
        setAttachments(data);
        // When editing, prefill each attachment from its child share (matched by
        // resource_type + resource_id). Attachments added to the agent after the
        // original share have no child row and get the mode's defaults.
        const childBefore = new Map(
          (initialShare?.children || []).map((child) => [
            `${child.resource_type}:${child.resource_id}`,
            child,
          ])
        );
        const initial = {};
        (data.folders_dim1 || []).forEach((item) => {
          initial[`folder:${item.id}`] = childBefore.get(`folder_dim1:${item.id}`)?.permission || 'read';
        });
        (data.folders_dim3 || []).forEach((item) => {
          initial[`folder:${item.id}`] = childBefore.get(`folder_dim3:${item.id}`)?.permission || 'read';
        });
        (data.composio_apps || []).forEach((item) => {
          initial[`composio:${item.id}`] = childBefore.get(`composio_app:${item.id}`)?.options?.auth_mode || composioDefault;
        });
        setChildSettings(initial);
        // A partner share that already carries a custom tool keeps it ticked.
        const tools = {};
        (data.custom_tools || []).forEach((item) => {
          if (childBefore.has(`custom_tool:${item.id}`)) tools[item.id] = true;
        });
        setIncludeTools(tools);
      } else if (isAgent && attachmentResult.status === 'rejected') {
        setError(describeApiError(attachmentResult.reason, 'Could not load the agent attachments.'));
      }
      setLoading(false);
    })();

    return () => { cancelled = true; };
    // partnerName/homeCompanyId only shape labels; reloadNonce refreshes the pickers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resource?.id, isAgent, canLoadUsers, canLoadRoles, canLoadPartnerCompanies, canLoadPartnerUsers, canWithin, canAcross, isEditing, initialShare, initialScope, reloadNonce]);

  const switchScope = (next) => {
    if (!next || next === scope) return;
    setScope(next);
    setSelected(new Set());
    setSearch('');
    setError('');
    // Each mode has its own safe defaults (§6.3).
    const composioDefault = next === 'partner' ? 'without_auth' : 'with_auth';
    setAuthMode(composioDefault);
    setChildSettings((old) => {
      const updated = { ...old };
      Object.keys(updated).forEach((key) => {
        if (key.startsWith('composio:')) updated[key] = composioDefault;
      });
      return updated;
    });
    setIncludeTools({});
    if (next === 'partner') setMemoryMode('per_user');
  };

  const visibleAudiences = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return audiences;
    return audiences.filter((item) => `${item.name} ${item.subtitle}`.toLowerCase().includes(query));
  }, [audiences, search]);

  const toggleAudience = (key) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    setError('');
  };

  const buildChildren = () => {
    if (!isAgent || !attachments) return undefined;
    const composioDefault = isPartnerMode ? 'without_auth' : 'with_auth';
    return [
      ...(attachments.folders_dim1 || []).map((item) => ({
        resource_type: 'folder_dim1', resource_id: item.id,
        permission: childSettings[`folder:${item.id}`] || 'read',
      })),
      // Sent only for a shared-memory share; on a per-user share the backend
      // would skip the folder and answer with a warning.
      ...(sharesOwnerDim3 ? attachments.folders_dim3 || [] : []).map((item) => ({
        resource_type: 'folder_dim3', resource_id: item.id,
        permission: childSettings[`folder:${item.id}`] || 'read',
      })),
      ...(attachments.composio_apps || []).map((item) => ({
        resource_type: 'composio_app', resource_id: item.id, permission: 'read',
        options: { auth_mode: childSettings[`composio:${item.id}`] || composioDefault },
      })),
      // Across companies a custom tool runs on the owner's stored credentials,
      // so it's only shared when explicitly ticked.
      ...(attachments.custom_tools || [])
        .filter((item) => !isPartnerMode || includeTools[item.id])
        .map((item) => ({
          resource_type: 'custom_tool', resource_id: item.id, permission: 'read',
          options: { auth_mode: 'with_auth' },
        })),
    ];
  };

  // Edit-mode save guard (spec: confirmation dialogs). Returns the list of
  // consequences that deserve an explicit confirm before re-sharing.
  const collectEditWarnings = () => {
    if (!isEditing) return [];
    const warnings = [];
    if (isAgent && !isPartnerMode && (initialShare.options?.memory_mode || 'per_user') !== memoryMode) {
      warnings.push(
        memoryMode === 'shared'
          ? 'Switching to a shared conversation moves where history is stored — recipients will appear to lose their previous private chats with this agent.'
          : 'Switching to private conversations moves where history is stored — recipients will appear to lose the shared chat history with this agent.'
      );
    }
    const childBefore = new Map(
      (initialShare.children || []).map((child) => [
        `${child.resource_type}:${child.resource_id}`,
        child,
      ])
    );
    const upgradedFolders = [
      ...(attachments?.folders_dim1 || []).map((item) => ({ ...item, dim: 'folder_dim1' })),
      ...(sharesOwnerDim3 ? attachments?.folders_dim3 || [] : []).map((item) => ({ ...item, dim: 'folder_dim3' })),
    ].filter((item) => {
      const before = childBefore.get(`${item.dim}:${item.id}`)?.permission || 'read';
      const after = childSettings[`folder:${item.id}`] || 'read';
      return before === 'read' && after === 'write';
    });
    if (upgradedFolders.length) {
      warnings.push(
        `Write access on ${upgradedFolders.map((item) => `"${item.name}"`).join(', ')}: recipients gain add/remove rights on the owner's folder files.`
      );
    }
    if (
      !isAgent &&
      String(resource?.type || '').startsWith('folder_') &&
      (initialShare.permission || 'read') === 'read' &&
      permission === 'write'
    ) {
      warnings.push("Write access on this folder: recipients gain add/remove rights on the owner's files.");
    }
    return warnings;
  };

  const finish = (result) => {
    onSuccess?.(result);
    onClose?.();
  };

  const submit = async () => {
    // Pre-submit validation mirroring the backend rules: ≥1 target, and every
    // target that needs an id carries a numeric principal_id.
    let targets;
    if (isEditing) {
      const target = buildTarget(initialShare.principal_type, sharePrincipalId(initialShare), editScope);
      if (!target) {
        setError(
          "This share's audience id is not available, so it can't be re-shared from here. Use the inline access controls, or revoke and share again."
        );
        return;
      }
      targets = [target];
    } else {
      const chosen = audiences.filter((item) => selected.has(item.key));
      const missingIds = chosen
        .filter((item) => needsPrincipalId(item.type, scope) && toNumericId(item.id) === null)
        .map((item) => item.name);
      if (missingIds.length) {
        setError(`Missing id for: ${missingIds.join(', ')}. Deselect them or reopen the dialog.`);
        return;
      }
      targets = chosen.map((item) => buildTarget(item.type, item.id, scope)).filter(Boolean);
    }
    if (!targets.length) {
      setError(isPartnerMode ? 'Select at least one audience.' : 'Select at least one company, role, or user.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        resource_type: resource.type,
        // Schema declares int — don't lean on Pydantic coercing "26".
        resource_id: toNumericId(resource.id) ?? resource.id,
        targets,
        permission: resource.type === 'composio_app' ? 'read' : permission,
      };
      if (isPartnerMode) payload.share_scope = 'partner';
      if (isAgent) {
        // Shared memory would mix different companies' conversations (§6.3).
        payload.options = { memory_mode: isPartnerMode ? 'per_user' : memoryMode };
        payload.children = buildChildren();
      } else if (resource.type === 'composio_app' || resource.type === 'custom_tool') {
        payload.options = { auth_mode: resource.type === 'custom_tool' ? 'with_auth' : authMode };
      }
      const result = await resourceSharingService.createShare(payload);
      const warnings = Array.isArray(result?.warnings) ? result.warnings : [];
      // §6.4: show every warning in a yellow panel after saving; don't block.
      if (isPartnerMode && warnings.length) {
        setDoneResult(result);
        return;
      }
      finish(result);
    } catch (err) {
      const rules = shareErrors(partnerName);
      setError(mapApiError(err, rules, 'Sharing failed.'));
      // A company or person left the partner meanwhile — refresh the picker.
      if (err?.status === 400 && /does not belong to the partner/i.test(err?.data?.error || err?.message || '')) {
        setReloadNonce((n) => n + 1);
      }
    } finally {
      setSaving(false);
    }
  };

  // Entry point for the primary button: in edit mode, surface consequence
  // warnings (memory-mode move, folder read→write) for confirmation first.
  const handleSave = () => {
    const warnings = collectEditWarnings();
    if (warnings.length) {
      setPendingWarnings(warnings);
      return;
    }
    submit();
  };

  // Same trap as childSummary in ManageSharingDialog: `attachments.folders_dim1`
  // comes back as null when there are none, and a default parameter only fills
  // in for undefined — so `items.length` would throw. Coerce, don't default.
  const renderFolderGroup = (title, type, rawItems, note) => {
    const items = Array.isArray(rawItems) ? rawItems : [];
    return items.length > 0 && (
    <Box className={styles.section}>
      <Typography className={styles.sectionTitle}>{title}</Typography>
      {note && <Typography variant="body2" color="text.secondary">{note}</Typography>}
      <Typography variant="body2" color="text.secondary">
        Write lets recipients add or remove files. Changes are visible to everyone with access.
      </Typography>
      <Box className={styles.attachmentList}>
        {items.map((item) => (
          <Box className={styles.attachmentRow} key={`${type}-${item.id}`}>
            <Box className={styles.attachmentMeta}>
              <Typography className={styles.attachmentName}>{item.name}</Typography>
              {item.description && <Typography variant="caption" color="text.secondary">{item.description}</Typography>}
            </Box>
            <Select
              size="small"
              value={childSettings[`folder:${item.id}`] || 'read'}
              onChange={(event) => setChildSettings((old) => ({ ...old, [`folder:${item.id}`]: event.target.value }))}
              aria-label={`${item.name} permission`}
            >
              <MenuItem value="read">Read</MenuItem>
              <MenuItem value="write">Write</MenuItem>
            </Select>
          </Box>
        ))}
      </Box>
    </Box>
    );
  };

  const typeLabel = resourceLabels[resource?.type] || 'resource';
  const title = isEditing
    ? `Edit sharing — ${typeLabel}`
    : isPartnerMode
    ? `Share ${typeLabel} across companies`
    : `Share ${typeLabel}`;

  // Partner shares can be (re)made only by partner staff; a client resource can
  // only go across companies.
  const cannotShare = !isEditing && !canWithin && !canAcross;
  const composioDefault = isPartnerMode ? 'without_auth' : 'with_auth';

  // ── Post-save warnings view (partner mode) ──
  if (doneResult) {
    return (
      <Dialog open={open} onClose={() => finish({ ...doneResult, warnings: [] })} maxWidth="sm" fullWidth PaperProps={{ className: styles.dialogPaper }}>
        <DialogTitle className={styles.dialogTitle}>
          <Typography variant="h6" sx={{ fontWeight: 750 }}>Shared — with notes</Typography>
        </DialogTitle>
        <DialogContent className={styles.content}>
          <Typography variant="body2" color="text.secondary">
            {resource?.name || 'The resource'} was shared across {partnerName}'s companies.
          </Typography>
          {(doneResult.warnings || []).map((warning) => (
            <Alert severity="warning" key={warning}>{warning}</Alert>
          ))}
        </DialogContent>
        <DialogActions className={styles.actions}>
          {/* The host would toast these again — they've just been read here. */}
          <Button variant="contained" onClick={() => finish({ ...doneResult, warnings: [] })}>Done</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth PaperProps={{ className: styles.dialogPaper }}>
      <DialogTitle className={styles.dialogTitle}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 750 }}>{title}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{resource?.name || 'Untitled resource'}</Typography>
        </Box>
        {isAgent && (
          <Box className={styles.steps}>
            <span className={`${styles.step} ${step === 0 ? styles.stepActive : ''}`}>1 Audience</span>
            <span className={`${styles.step} ${step === 1 ? styles.stepActive : ''}`}>2 Attachments</span>
          </Box>
        )}
      </DialogTitle>

      <DialogContent className={styles.content}>
        {!canWithin && (
          <Alert severity="info">
            {SHARING_COPY.clientOwnedBanner(resource?.ownerCompanyName || 'another company')}
          </Alert>
        )}

        {cannotShare ? (
          <Alert severity="error">Can only share resources within your own company</Alert>
        ) : loading ? (
          <Box sx={{ minHeight: 220, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>
        ) : step === 0 ? (
          <>
            {showModeSwitch && (
              <ToggleButtonGroup
                exclusive
                size="small"
                color="primary"
                value={scope}
                onChange={(_, next) => switchScope(next)}
                sx={{ alignSelf: 'flex-start', flexWrap: 'wrap' }}
              >
                <ToggleButton value="company" sx={{ textTransform: 'none' }}>
                  {SHARING_COPY.modeWithin(withinName)}
                </ToggleButton>
                <ToggleButton value="partner" sx={{ textTransform: 'none' }}>
                  {SHARING_COPY.modeAcross(partnerName)}
                </ToggleButton>
              </ToggleButtonGroup>
            )}

            {isEditing ? (
              <Box className={styles.section}>
                <Typography className={styles.sectionTitle}>Audience</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  {(() => {
                    const Icon = audienceIcon(initialShare.principal_type);
                    return (
                      <>
                        <Icon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ fontWeight: 650 }}>
                          {principalLabel(initialShare, { partnerName, homeCompanyId })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {isPartnerMode ? 'Across companies' : initialShare.principal_display?.type || initialShare.principal_type}
                        </Typography>
                      </>
                    );
                  })()}
                </Box>
                <FormHelperText>
                  The audience can't be changed here — revoke this share and create a new one to share with someone else.
                </FormHelperText>
                {editPrincipalMissing && (
                  <Alert severity="error" sx={{ mt: 1.5 }}>
                    This share's audience id is not available, so it can't be re-shared from here.
                    Use the inline access controls, or revoke and share again.
                  </Alert>
                )}
              </Box>
            ) : (
              <Box className={styles.section}>
                <Typography className={styles.sectionTitle}>Who should have access?</Typography>
                <Typography variant="body2" color="text.secondary">
                  {isPartnerMode
                    ? `Choose all of ${partnerName}'s companies, specific companies, or specific people. ${SHARING_COPY.targetCompaniesHelp}`
                    : 'Choose any combination of the company, roles, and specific users.'}
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  placeholder={isPartnerMode ? 'Search companies or people…' : 'Search roles or users…'}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  sx={{ mt: 1.5 }}
                />
                <Box className={styles.audienceList}>
                  {visibleAudiences.map((item) => (
                    <AudienceOption key={item.key} audience={item} checked={selected.has(item.key)} onChange={() => toggleAudience(item.key)} />
                  ))}
                </Box>
              </Box>
            )}

            {resource?.type !== 'composio_app' && (
              <Box className={styles.section}>
                <Typography className={styles.sectionTitle}>{isAgent ? 'Agent access' : 'Permission'}</Typography>
                {isAgent && (
                  <Typography variant="body2" color="text.secondary">
                    Controls access to the agent itself. Folder and tool access is set per attachment in the next step.
                  </Typography>
                )}
                <RadioGroup row value={permission} onChange={(event) => setPermission(event.target.value)}>
                  <FormControlLabel value="read" control={<Radio />} label={isAgent ? 'Read (run & chat)' : 'Read'} />
                  <FormControlLabel value="write" control={<Radio />} label={isAgent ? 'Write (also edit configuration)' : 'Write'} />
                </RadioGroup>
                {isPartnerMode && (
                  <FormHelperText>
                    {permission === 'write' ? SHARING_COPY.writeHelp : SHARING_COPY.readHelp}
                  </FormHelperText>
                )}
              </Box>
            )}

            {isAgent && (
              <Box className={styles.section}>
                <Typography className={styles.sectionTitle}>Conversation memory</Typography>
                {isPartnerMode ? (
                  // Only private memory across companies (§6.3) — no choice to make.
                  <Typography variant="body2">{SHARING_COPY.memoryPerUser}</Typography>
                ) : (
                  <>
                    <RadioGroup value={memoryMode} onChange={(event) => setMemoryMode(event.target.value)}>
                      <FormControlLabel value="per_user" control={<Radio />} label="Private conversations — each recipient has separate history" />
                      <FormControlLabel value="shared" control={<Radio />} label="Shared conversation — everyone shares one context" />
                    </RadioGroup>
                    {memoryMode === 'shared' && <Alert severity="warning">Recipients can influence the same ongoing context. Use this only for collaborative workflows.</Alert>}
                  </>
                )}
              </Box>
            )}

            {resource?.type === 'composio_app' && (
              <Box className={styles.section}>
                <Typography className={styles.sectionTitle}>How should recipients use this integration?</Typography>
                <RadioGroup value={authMode} onChange={(event) => setAuthMode(event.target.value)}>
                  {isPartnerMode ? (
                    <>
                      <FormControlLabel value="without_auth" control={<Radio />} label={SHARING_COPY.composioWithoutAuth} />
                      <FormControlLabel value="with_auth" control={<Radio />} label={SHARING_COPY.composioWithAuth} />
                    </>
                  ) : (
                    <>
                      <FormControlLabel value="with_auth" control={<Radio />} label="Use the owner's connection — no setup for recipients" />
                      <FormControlLabel value="without_auth" control={<Radio />} label="Each user connects their own account" />
                    </>
                  )}
                </RadioGroup>
                {isPartnerMode && authMode === 'with_auth' && (
                  <Alert severity="warning">{SHARING_COPY.ownerConnectionWarning}</Alert>
                )}
              </Box>
            )}

            {resource?.type === 'custom_tool' && (
              <Alert severity={isPartnerMode ? 'warning' : 'info'}>
                {isPartnerMode
                  ? SHARING_COPY.customToolTooltip
                  : "This custom tool runs on the resource owner's credentials. Recipient-managed authentication is not available in this version."}
              </Alert>
            )}

            {directoryWarnings.length > 0 && (
              <Alert severity="warning">
                {isPartnerMode
                  ? `Could not load ${directoryWarnings.join(' and ')}. Sharing with all of ${partnerName}'s companies remains available.`
                  : `Could not load ${directoryWarnings.join(' and ')}. Company-wide sharing remains available, but role/user targets require the corresponding company directory permission.`}
              </Alert>
            )}
          </>
        ) : (
          <>
            {(attachments?.warnings || []).map((warning) => <Alert severity="warning" key={warning}>{warning}</Alert>)}
            {renderFolderGroup('Rules book (D1)', 'folder_dim1', attachments?.folders_dim1, SHARED_KB_COPY.shareDim1Note)}
            {sharesOwnerDim3 && renderFolderGroup('Customer Data (D3)', 'folder_dim3', attachments?.folders_dim3)}

            {(attachments?.composio_apps || []).length > 0 && (
              <Box className={styles.section}>
                <Typography className={styles.sectionTitle}>Composio integrations</Typography>
                <Typography variant="body2" color="text.secondary">Choose whether recipients use the resource owner's connection or connect their own account.</Typography>
                <Box className={styles.attachmentList}>
                  {(attachments.composio_apps || []).map((item) => {
                    const mode = childSettings[`composio:${item.id}`] || composioDefault;
                    return (
                      <Box className={styles.attachmentRow} key={`composio-${item.id}`}>
                        <Box className={styles.attachmentMeta}>
                          <Typography className={styles.attachmentName}>{item.app_name}</Typography>
                          <Typography variant="caption" color={item.status === 'active' ? 'text.secondary' : 'warning.main'}>
                            {item.status === 'active' ? 'Connected' : `The owner's connection is ${item.status || 'unavailable'}`}
                          </Typography>
                        </Box>
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                          <InputLabel>Authentication</InputLabel>
                          <Select
                            label="Authentication"
                            value={mode}
                            onChange={(event) => setChildSettings((old) => ({ ...old, [`composio:${item.id}`]: event.target.value }))}
                          >
                            <MenuItem value="with_auth">{isPartnerMode ? SHARING_COPY.composioWithAuth : "Use the owner's connection"}</MenuItem>
                            <MenuItem value="without_auth">{isPartnerMode ? SHARING_COPY.composioWithoutAuth : 'Each user connects their own'}</MenuItem>
                          </Select>
                        </FormControl>
                        {isPartnerMode && mode === 'with_auth' && (
                          <Alert severity="warning" sx={{ width: '100%' }}>{SHARING_COPY.ownerConnectionWarning}</Alert>
                        )}
                        {item.status !== 'active' && mode === 'with_auth' && (
                          <Alert severity="warning" sx={{ width: '100%' }}>
                            The owner's {item.app_name} connection is not active. Recipients will see it as temporarily unavailable.
                          </Alert>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {(attachments?.custom_tools || []).length > 0 && (
              <Box className={styles.section}>
                <Typography className={styles.sectionTitle}>Custom tools</Typography>
                {isPartnerMode && (
                  <Typography variant="body2" color="text.secondary">Not shared unless you tick them.</Typography>
                )}
                <Box className={styles.attachmentList}>
                  {(attachments.custom_tools || []).map((item) => (
                    <Box className={styles.attachmentRow} key={`custom-${item.id}`}>
                      {isPartnerMode ? (
                        <Tooltip title={SHARING_COPY.customToolTooltip} arrow placement="top-start">
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={!!includeTools[item.id]}
                                onChange={(event) => setIncludeTools((old) => ({ ...old, [item.id]: event.target.checked }))}
                              />
                            }
                            label={SHARING_COPY.customToolInclude(item.name)}
                          />
                        </Tooltip>
                      ) : (
                        <Box className={styles.attachmentMeta}>
                          <Typography className={styles.attachmentName}>{item.name}</Typography>
                          <Typography variant="caption" color="text.secondary">Runs on the owner's credentials</Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Under the attachments list: the agent has D3 folders, but they
                stay the owner's on a per-user share. */}
            {!sharesOwnerDim3 && (attachments?.folders_dim3 || []).length > 0 && (
              <Alert severity="info">
                {SHARED_KB_COPY.shareDim3Note}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {SHARED_KB_COPY.shareDim3Hint}
                </Typography>
              </Alert>
            )}

            {!attachments?.folders_dim1?.length && !attachments?.folders_dim3?.length && !attachments?.composio_apps?.length && !attachments?.custom_tools?.length && (
              <Alert severity="info">This agent has no attached folders or tools. It can be shared as-is.</Alert>
            )}
            {/* Runner pays across companies (§1.7); in-company shares are unchanged. */}
            <Alert severity="info">
              {isPartnerMode
                ? SHARING_COPY.runnerPays
                : "Recipients use the owner's configured LLM credentials and quota when running this agent."}
            </Alert>
          </>
        )}

        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>

      <DialogActions className={styles.actions}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        {isAgent && step === 1 && <Button onClick={() => setStep(0)} disabled={saving}>Back</Button>}
        {isAgent && step === 0 ? (
          <Button
            variant="contained"
            onClick={() => (isEditing || selected.size ? setStep(1) : setError('Select at least one audience.'))}
            disabled={loading || !attachments || editPrincipalMissing || cannotShare}
          >
            Continue
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <Share />}
            onClick={handleSave}
            disabled={saving || loading || editPrincipalMissing || cannotShare}
          >
            {saving ? (isEditing ? 'Saving…' : 'Sharing…') : isEditing ? 'Save changes' : 'Share'}
          </Button>
        )}
      </DialogActions>

      {/* Consequence confirmation before re-saving an edited share (spec: memory
          moves + folder read→write need an explicit confirm). */}
      <Dialog open={Boolean(pendingWarnings)} onClose={() => setPendingWarnings(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm these changes?</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {(pendingWarnings || []).map((warning) => (
            <Alert severity="warning" key={warning}>{warning}</Alert>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingWarnings(null)}>Go back</Button>
          <Button
            variant="contained"
            onClick={() => { setPendingWarnings(null); submit(); }}
          >
            Save changes
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ResourceShareDialog;
